import React, { useState, useEffect } from 'react';
import { Calendar, Clock, User, Video, MapPin, X, Check, RefreshCw, UserCog, XCircle, DollarSign, Bell, MessageSquare, Phone, Mail } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { engineers } from '../../mock';
import { parseLocalDate } from '../../lib/utils';
import { MoveSessionModal } from './MoveSessionModal';

const API = '/api';

const UpcomingSessionsCalendar = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'today', 'week', 'month'
  const [statusFilter, setStatusFilter] = useState('upcoming'); // 'upcoming', 'completed'
  const [resendingEmail, setResendingEmail] = useState(null);
  const [moveSession, setMoveSession] = useState(null);
  const [clientResponses, setClientResponses] = useState([]);
  const [showResponsesPanel, setShowResponsesPanel] = useState(false);
  
  // Modal states
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [showChangeEngineerModal, setShowChangeEngineerModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [rescheduleData, setRescheduleData] = useState({ date: '', time: '' });
  const [notifyClient, setNotifyClient] = useState(false);
  const [newEngineerId, setNewEngineerId] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  
  // Multi-slot reschedule state
  const [slotOptions, setSlotOptions] = useState([{ date: '', time: '' }, { date: '', time: '' }, { date: '', time: '' }]);
  
  // Manual reschedule state
  const [isManualReschedule, setIsManualReschedule] = useState(false);
  const [manualDate, setManualDate] = useState('');
  const [manualTime, setManualTime] = useState('');
  const [sendConfirmation, setSendConfirmation] = useState(true);
  
  // Availability check state
  const [bookedSlots, setBookedSlots] = useState([]);
  const [loadingAvailability, setLoadingAvailability] = useState(false);

  useEffect(() => {
    fetchSessions();
    fetchClientResponses();
  }, []);

  const fetchClientResponses = async () => {
    try {
      const response = await axios.get(`${API}/bookings/client-responses`);
      setClientResponses(response.data);
    } catch (error) {
      console.error('Error fetching client responses:', error);
    }
  };

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/bookings`);
      
      // Sort: upcoming sessions first (ascending by date), then past sessions
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const sorted = response.data.sort((a, b) => {
        const dateA = parseLocalDate(a.date);
        const dateB = parseLocalDate(b.date);
        
        // Helper to parse time like "8:00 PM" or "12:00 AM"
        const parseTime = (timeStr) => {
          if (!timeStr) return 0;
          const match = timeStr.match(/(\d+):(\d+)\s*(AM|PM)/i);
          if (!match) return 0;
          let hours = parseInt(match[1]);
          const minutes = parseInt(match[2]);
          const period = match[3].toUpperCase();
          if (period === 'PM' && hours !== 12) hours += 12;
          if (period === 'AM' && hours === 12) hours = 0;
          return hours * 60 + minutes;
        };
        
        const isAUpcoming = dateA >= today;
        const isBUpcoming = dateB >= today;
        
        // Upcoming bookings come first
        if (isAUpcoming && !isBUpcoming) return -1;
        if (!isAUpcoming && isBUpcoming) return 1;
        
        // For upcoming: sort ascending (soonest first)
        if (isAUpcoming && isBUpcoming) {
          if (dateA.getTime() !== dateB.getTime()) {
            return dateA - dateB;
          }
          // Same date - sort by time
          return parseTime(a.time) - parseTime(b.time);
        }
        
        // For past: sort descending (most recent first)
        return dateB - dateA;
      });
      
      setSessions(sorted);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      setLoading(false);
    }
  };

  const handleResendEmail = async (session) => {
    try {
      setResendingEmail(session.id);
      const response = await axios.post(`${API}/bookings/${session.id}/resend-email`);
      
      if (response.data.success) {
        toast.success(`Confirmation email sent to ${session.email}!`);
      } else {
        toast.error('Failed to send email. Please check your email configuration.');
      }
    } catch (error) {
      console.error('Error resending email:', error);
      toast.error(error.response?.data?.detail || 'Failed to send email. Please try again.');
    } finally {
      setResendingEmail(null);
    }
  };

  const handleAdminConfirm = async (sessionId) => {
    if (!window.confirm('Confirm this booking?')) return;
    
    try {
      setProcessing(true);
      await axios.post(`${API}/bookings/${sessionId}/admin-confirm`);
      toast.success('Booking confirmed!');
      fetchSessions();
    } catch (error) {
      console.error('Error confirming:', error);
      toast.error(error.response?.data?.detail || 'Failed to confirm booking');
    } finally {
      setProcessing(false);
    }
  };


  const handleDeclineBooking = async (session) => {
    const confirmMessage = `Are you sure you want to DECLINE this booking?\n\nCustomer: ${session.name}\nDate: ${session.date}\nTime: ${session.time}\n\nA polite decline email will be sent to the customer and the booking will be removed.`;
    
    if (!window.confirm(confirmMessage)) return;
    
    try {
      setProcessing(true);
      await axios.post(`${API}/bookings/${session.id}/decline`);
      toast.success('Booking declined and customer notified');
      fetchSessions(); // Refresh the list
    } catch (error) {
      console.error('Error declining booking:', error);
      toast.error(error.response?.data?.detail || 'Failed to decline booking');
    } finally {
      setProcessing(false);
    }
  };


  const handleMarkPaid = async (session) => {
    const paymentMethods = ['cash', 'zelle', 'venmo', 'cashapp', 'check', 'other'];
    const method = window.prompt(
      `Mark ${session.name}'s booking as PAID.\n\nPayment method:\n- cash\n- zelle\n- venmo\n- cashapp\n- check\n- other\n\nEnter payment method:`,
      'cash'
    );
    
    if (!method) return;
    
    const cleanMethod = method.toLowerCase().trim();
    
    try {
      setProcessing(true);
      await axios.post(`${API}/bookings/${session.id}/mark-paid?payment_method=${cleanMethod}`);
      toast.success(`Marked as paid via ${cleanMethod}`);
      fetchSessions(); // Refresh the list
    } catch (error) {
      console.error('Error marking as paid:', error);
      toast.error(error.response?.data?.detail || 'Failed to mark as paid');
    } finally {
      setProcessing(false);
    }
  };


  const handleMarkDepositPaid = async (session) => {
    const paymentMethods = ['cash', 'zelle', 'venmo', 'cashapp', 'check', 'other'];
    const method = window.prompt(
      `Mark ${session.name}'s DEPOSIT as PAID.\n\nPayment method:\n- cash\n- zelle\n- venmo\n- cashapp\n- check\n- other\n\nEnter payment method:`,
      'cash'
    );
    
    if (!method) return;
    
    const cleanMethod = method.toLowerCase().trim();
    
    try {
      setProcessing(true);
      await axios.post(`${API}/bookings/${session.id}/mark-deposit-paid?payment_method=${cleanMethod}`);
      toast.success(`Deposit marked as paid via ${cleanMethod}`);
      fetchSessions(); // Refresh the list
    } catch (error) {
      console.error('Error marking deposit as paid:', error);
      toast.error(error.response?.data?.detail || 'Failed to mark deposit as paid');
    } finally {
      setProcessing(false);
    }
  };

  // Open payment modal for recording final payment on completed sessions
  const openPaymentModal = (session) => {
    setSelectedSession(session);
    // Pre-fill with remaining balance if deposit was paid
    const remaining = session.price - (session.deposit || 0);
    setPaymentAmount(remaining > 0 ? remaining.toFixed(2) : session.price.toFixed(2));
    setPaymentMethod('cash');
    setShowPaymentModal(true);
  };

  // Handle recording final payment
  const handleRecordPayment = async () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }

    try {
      setProcessing(true);
      await axios.post(`${API}/bookings/${selectedSession.id}/record-final-payment`, {
        amount: parseFloat(paymentAmount),
        payment_method: paymentMethod
      });
      toast.success(`Payment of $${paymentAmount} recorded successfully`);
      setShowPaymentModal(false);
      setPaymentAmount('');
      fetchSessions();
    } catch (error) {
      console.error('Error recording payment:', error);
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setProcessing(false);
    }
  };



  const openRescheduleModal = (session) => {
    setSelectedSession(session);
    setRescheduleData({ date: session.date, time: session.time });
    // Initialize with 3 empty slots
    setSlotOptions([{ date: '', time: '' }, { date: '', time: '' }, { date: '', time: '' }]);
    setNotifyClient(true); // Default to sending email for multi-slot
    setIsManualReschedule(false); // Start in offer mode
    setManualDate('');
    setManualTime('');
    setSendConfirmation(true);
    setBookedSlots([]);
    setShowRescheduleModal(true);
  };
  
  // Fetch availability for a specific date
  const fetchAvailabilityForDate = async (date, bookingId) => {
    if (!date) return;
    
    try {
      setLoadingAvailability(true);
      const response = await axios.get(`${API}/bookings/availability-for-reschedule/${date}?exclude_booking_id=${bookingId || ''}`);
      setBookedSlots(response.data.all_unavailable_times || []);
    } catch (error) {
      console.error('Error fetching availability:', error);
      setBookedSlots([]);
    } finally {
      setLoadingAvailability(false);
    }
  };
  
  // Handle manual date change - fetch availability
  const handleManualDateChange = (date) => {
    setManualDate(date);
    setManualTime(''); // Reset time when date changes
    if (date && selectedSession) {
      fetchAvailabilityForDate(date, selectedSession.id);
    }
  };
  
  // Handle manual reschedule submission
  const handleManualReschedule = async () => {
    if (!manualDate || !manualTime) {
      toast.error('Please select both date and time');
      return;
    }
    
    try {
      setProcessing(true);
      const response = await axios.put(
        `${API}/bookings/${selectedSession.id}/manual-reschedule`,
        {
          new_date: manualDate,
          new_time: manualTime,
          send_confirmation: sendConfirmation
        }
      );
      
      toast.success(`Booking rescheduled to ${manualDate} at ${manualTime}${response.data.email_sent ? ' - Confirmation sent!' : ''}`);
      setShowRescheduleModal(false);
      fetchSessions();
    } catch (error) {
      console.error('Error in manual reschedule:', error);
      toast.error(error.response?.data?.detail || 'Failed to reschedule booking');
    } finally {
      setProcessing(false);
    }
  };

  const handleReschedule = async () => {
    // Filter out empty slots
    const validSlots = slotOptions.filter(s => s.date && s.time);
    
    if (validSlots.length === 0) {
      toast.error('Please add at least one time slot option');
      return;
    }

    try {
      setProcessing(true);
      const response = await axios.post(
        `${API}/bookings/${selectedSession.id}/reschedule-offer`,
        {
          slot_options: validSlots
        }
      );
      
      toast.success(`Reschedule offer sent with ${validSlots.length} options! Original slot reopened.`);
      setShowRescheduleModal(false);
      fetchSessions();
    } catch (error) {
      console.error('Error sending reschedule offer:', error);
      toast.error(error.response?.data?.detail || 'Failed to send reschedule offer');
    } finally {
      setProcessing(false);
    }
  };
  
  const updateSlotOption = (index, field, value) => {
    const newSlots = [...slotOptions];
    newSlots[index] = { ...newSlots[index], [field]: value };
    setSlotOptions(newSlots);
  };
  
  const addSlotOption = () => {
    if (slotOptions.length < 5) {
      setSlotOptions([...slotOptions, { date: '', time: '' }]);
    }
  };
  
  const removeSlotOption = (index) => {
    if (slotOptions.length > 1) {
      setSlotOptions(slotOptions.filter((_, i) => i !== index));
    }
  };

  const openChangeEngineerModal = (session) => {
    setSelectedSession(session);
    setNewEngineerId(session.engineer_id);
    setShowChangeEngineerModal(true);
  };

  const handleChangeEngineer = async () => {
    if (!newEngineerId || newEngineerId === selectedSession.engineer_id) {
      toast.error('Please select a different engineer');
      return;
    }

    const newEngineer = engineers.find(e => e.id === newEngineerId);
    if (!newEngineer) return;

    try {
      setProcessing(true);
      await axios.patch(
        `${API}/bookings/${selectedSession.id}/change-engineer`,
        {
          new_engineer_id: newEngineerId,
          new_engineer_name: newEngineer.name
        }
      );
      
      toast.success(`Engineer changed to ${newEngineer.name}`);
      setShowChangeEngineerModal(false);
      fetchSessions();
    } catch (error) {
      console.error('Error changing engineer:', error);
      toast.error(error.response?.data?.detail || 'Failed to change engineer');
    } finally {
      setProcessing(false);
    }
  };

  const getFilteredSessions = () => {
    return sessions.filter(session => {
      // Parse date as local date to avoid timezone shifts
      const [year, month, day] = session.date.split('-').map(Number);
      const sessionDate = new Date(year, month - 1, day);
      const [hours, minutes] = session.time.split(':');
      const period = session.time.includes('PM') || session.time.includes('AM') ? session.time.slice(-2) : null;
      let hour24 = parseInt(hours);
      
      if (period === 'PM' && hour24 !== 12) hour24 += 12;
      if (period === 'AM' && hour24 === 12) hour24 = 0;
      
      sessionDate.setHours(hour24, parseInt(minutes) || 0, 0, 0);
      
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const weekFromNow = new Date(today);
      weekFromNow.setDate(weekFromNow.getDate() + 7);
      const monthFromNow = new Date(today);
      monthFromNow.setMonth(monthFromNow.getMonth() + 1);
      
      // Check if session time has passed (completed based on date/time, not status)
      const isSessionPast = sessionDate < now;
      
      // Hide cancelled sessions from both views
      if (session.status === 'cancelled') return false;
      
      // Filter by upcoming vs completed based on session date/time
      // Upcoming: sessions that haven't happened yet
      // Completed: sessions that have passed (regardless of status field)
      if (statusFilter === 'upcoming' && isSessionPast) return false;
      if (statusFilter === 'completed' && !isSessionPast) return false;

      // Filter by time range
      switch(filter) {
        case 'today':
          return sessionDate.toDateString() === today.toDateString();
        case 'week':
          return statusFilter === 'upcoming' 
            ? (sessionDate >= today && sessionDate <= weekFromNow)
            : (sessionDate >= new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000) && sessionDate <= today);
        case 'month':
          return statusFilter === 'upcoming'
            ? (sessionDate >= today && sessionDate <= monthFromNow)
            : (sessionDate >= new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000) && sessionDate <= today);
        default:
          return true;
      }
    });
  };

  const filteredSessions = getFilteredSessions();

  const getSessionColor = (session) => {
    // Parse date as local date to avoid timezone shifts
    const [year, month, day] = session.date.split('-').map(Number);
    const sessionDate = new Date(year, month - 1, day);
    const [hours, minutes] = session.time.split(':');
    const period = session.time.includes('PM') || session.time.includes('AM') ? session.time.slice(-2) : null;
    let hour24 = parseInt(hours);
    
    if (period === 'PM' && hour24 !== 12) hour24 += 12;
    if (period === 'AM' && hour24 === 12) hour24 = 0;
    
    sessionDate.setHours(hour24, parseInt(minutes) || 0, 0, 0);
    
    const now = new Date();
    const isCompleted = sessionDate < now;
    
    // Completed sessions - green
    if (isCompleted) {
      return 'bg-green-500/20 border-green-500/50 text-green-300';
    }
    
    // Upcoming sessions - blue gradient
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffDays = Math.ceil((sessionDate - today) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'bg-blue-500/20 border-blue-500/50 text-blue-300'; // Today
    if (diffDays <= 3) return 'bg-blue-500/20 border-blue-400/50 text-blue-300'; // Within 3 days
    return 'bg-blue-500/20 border-blue-300/50 text-blue-300'; // Future
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {moveSession && <MoveSessionModal session={moveSession} onClose={() => setMoveSession(null)} onMoved={fetchSessions} />}
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h3 className="text-2xl font-bold text-white flex items-center gap-2">
            <Calendar className="text-purple-400" />
            {statusFilter === 'upcoming' ? 'Upcoming' : 'Completed'} Sessions ({filteredSessions.length})
          </h3>
          <p className="text-gray-400 text-sm mt-1">View and manage scheduled recording sessions</p>
        </div>

        {/* Filter Buttons */}
        <div className="flex flex-col gap-3">
          {/* Status Filter */}
          <div className="flex gap-2">
            <button
              onClick={() => setStatusFilter('upcoming')}
              className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                statusFilter === 'upcoming'
                  ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white'
                  : 'bg-green-900/20 text-gray-400 hover:text-white'
              }`}
            >
              Upcoming
            </button>
            <button
              onClick={() => setStatusFilter('completed')}
              className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                statusFilter === 'completed'
                  ? 'bg-gradient-to-r from-gray-600 to-slate-600 text-white'
                  : 'bg-gray-900/20 text-gray-400 hover:text-white'
              }`}
            >
              Completed
            </button>
          </div>
          
          {/* Time Filter */}
          <div className="flex gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                filter === 'all'
                  ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white'
                  : 'bg-purple-900/20 text-gray-400 hover:text-white'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilter('today')}
              className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                filter === 'today'
                  ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white'
                  : 'bg-purple-900/20 text-gray-400 hover:text-white'
              }`}
            >
              Today
            </button>
            <button
              onClick={() => setFilter('week')}
              className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                filter === 'week'
                  ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white'
                  : 'bg-purple-900/20 text-gray-400 hover:text-white'
              }`}
            >
              This Week
            </button>
            <button
              onClick={() => setFilter('month')}
              className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                filter === 'month'
                  ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white'
                  : 'bg-purple-900/20 text-gray-400 hover:text-white'
              }`}
            >
              This Month
            </button>
          </div>
          
          {/* Client Responses Button */}
          <button
            onClick={() => setShowResponsesPanel(!showResponsesPanel)}
            className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center gap-2 ${
              showResponsesPanel
                ? 'bg-gradient-to-r from-orange-600 to-red-600 text-white'
                : 'bg-orange-900/20 text-orange-400 hover:text-white border border-orange-500/30'
            }`}
          >
            <Bell size={16} />
            Client Responses
            {clientResponses?.pending_count > 0 && (
              <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                {clientResponses.pending_count}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Client Responses Panel */}
      {showResponsesPanel && (
        <div className="bg-orange-900/10 border border-orange-500/30 rounded-xl p-4 mb-6">
          <h3 className="text-lg font-bold text-orange-400 mb-4 flex items-center gap-2">
            <MessageSquare size={20} />
            Client Responses to Engineer Changes
          </h3>
          
          {clientResponses?.change_requests?.length === 0 ? (
            <p className="text-gray-400 text-center py-4">No engineer change requests yet</p>
          ) : (
            <div className="space-y-3">
              {clientResponses?.change_requests?.map((req, idx) => (
                <div 
                  key={idx}
                  className={`p-4 rounded-lg border ${
                    req.status === 'pending' 
                      ? 'bg-yellow-900/20 border-yellow-500/30' 
                      : req.status === 'confirm'
                        ? 'bg-green-900/20 border-green-500/30'
                        : req.status === 'cancel'
                          ? 'bg-red-900/20 border-red-500/30'
                          : 'bg-blue-900/20 border-blue-500/30'
                  }`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold text-white">{req.client_name}</p>
                      <p className="text-sm text-gray-400">
                        {req.old_engineer} → {req.new_engineer}
                      </p>
                      <p className="text-xs text-gray-500">
                        {req.session_date} at {req.session_time}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                        req.status === 'pending' 
                          ? 'bg-yellow-500/20 text-yellow-400' 
                          : req.status === 'confirm'
                            ? 'bg-green-500/20 text-green-400'
                            : req.status === 'cancel'
                              ? 'bg-red-500/20 text-red-400'
                              : 'bg-blue-500/20 text-blue-400'
                      }`}>
                        {req.status === 'pending' ? '⏳ Awaiting Response' 
                          : req.status === 'confirm' ? '✅ Confirmed'
                          : req.status === 'cancel' ? '❌ Cancelled'
                          : '🔄 Wants Reschedule'}
                      </span>
                      
                      {/* Contact buttons for follow-up */}
                      {req.status !== 'pending' && (
                        <div className="flex gap-2">
                          {req.client_phone && (
                            <a 
                              href={`tel:${req.client_phone}`}
                              className="p-2 bg-green-600/20 text-green-400 rounded-lg hover:bg-green-600/30 transition-colors"
                              title="Call client"
                            >
                              <Phone size={14} />
                            </a>
                          )}
                          <a 
                            href={`mailto:${req.client_email}`}
                            className="p-2 bg-blue-600/20 text-blue-400 rounded-lg hover:bg-blue-600/30 transition-colors"
                            title="Email client"
                          >
                            <Mail size={14} />
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {req.status === 'reschedule' && (
                    <p className="mt-2 text-sm text-orange-400 bg-orange-500/10 rounded p-2">
                      ⚠️ Client wants to reschedule. Please contact them to arrange a new time.
                    </p>
                  )}
                  {req.status === 'cancel' && (
                    <p className="mt-2 text-sm text-red-400 bg-red-500/10 rounded p-2">
                      ⚠️ Client cancelled due to engineer change. Process refund if applicable.
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
          
          {/* Needs Attention Section */}
          {clientResponses?.needs_attention?.length > 0 && (
            <div className="mt-4 pt-4 border-t border-orange-500/20">
              <h4 className="text-sm font-bold text-orange-300 mb-2">🔔 Needs Your Attention:</h4>
              <div className="space-y-2">
                {clientResponses.needs_attention.map((booking, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-orange-900/30 rounded-lg p-3">
                    <div>
                      <span className="text-white font-medium">{booking.name}</span>
                      <span className="text-gray-400 text-sm ml-2">
                        {booking.needs_rescheduling ? 'Needs rescheduling' : 
                         booking.status === 'cancelled' ? 'Cancelled - needs refund' : 
                         'Engineer change confirmed'}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      {booking.phone && (
                        <a href={`tel:${booking.phone}`} className="text-green-400 hover:text-green-300">
                          <Phone size={16} />
                        </a>
                      )}
                      <a href={`mailto:${booking.email}`} className="text-blue-400 hover:text-blue-300">
                        <Mail size={16} />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Sessions List */}
      {filteredSessions.length === 0 ? (
        <div className="bg-purple-900/10 border border-purple-500/20 rounded-lg p-12 text-center">
          <Calendar size={64} className="mx-auto text-gray-500 mb-4" />
          <p className="text-gray-400 text-lg">No {statusFilter} sessions</p>
          <p className="text-gray-500 text-sm mt-2">
            {statusFilter === 'upcoming' 
              ? 'Sessions will appear here once they\'re booked' 
              : 'Completed sessions will appear here after they\'ve finished'
            }
          </p>
        </div>
      ) : (
        <div className="grid gap-4 max-w-4xl">
          {filteredSessions.map((session) => (
            <div
              key={session.id}
              className={`border-2 rounded-lg p-3 sm:p-6 transition-all hover:shadow-lg ${getSessionColor(session)}`}
            >
              <div className="flex flex-col sm:flex-row items-start justify-between gap-3 sm:gap-4">
                {/* Left Side - Session Info */}
                <div className="flex-1 space-y-2 sm:space-y-3 min-w-0 w-full">
                  {/* Booking Number */}
                  {session.booking_number && (
                    <div className="text-xs sm:text-sm font-mono text-purple-400 font-bold">
                      {session.booking_number}
                    </div>
                  )}
                  
                  {/* Date & Time */}
                  <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-sm sm:text-base">
                    <div className="flex items-center gap-1 sm:gap-2">
                      <Calendar size={16} className="sm:w-5 sm:h-5" />
                      <span className="font-bold">
                        {(() => {
                          // Parse date as local date to avoid timezone shifts
                          const [year, month, day] = session.date.split('-').map(Number);
                          const localDate = new Date(year, month - 1, day);
                          return localDate.toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          });
                        })()}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2">
                      <Clock size={16} className="sm:w-5 sm:h-5" />
                      <span className="font-semibold">{session.time}</span>
                    </div>
                  </div>

                  {/* Client Info */}
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-sm">
                      <User size={16} className="flex-shrink-0" />
                      <span className="text-white font-semibold">{session.name}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-300 pl-6">
                      <span className="break-all">{session.email}</span>
                    </div>
                    {session.phone && (
                      <a 
                        href={`tel:${session.phone}`}
                        className="flex items-center gap-2 text-sm text-green-400 hover:text-green-300 pl-6"
                      >
                        <Phone size={14} className="flex-shrink-0" />
                        <span className="font-medium">{session.phone}</span>
                      </a>
                    )}
                  </div>

                  {/* Engineer */}
                  <div className="flex items-center gap-2 text-sm">
                    <Video size={16} className="flex-shrink-0" />
                    <span className="text-gray-300">Engineer: <span className="font-semibold text-white">{session.engineer_name}</span></span>
                  </div>

                  {/* Service */}
                  <div className="flex items-center gap-2 text-xs sm:text-sm">
                    <MapPin size={14} className="sm:w-4 sm:h-4 flex-shrink-0" />
                    <span className="text-gray-300">{session.service_name}</span>
                  </div>

                  {/* Add-ons */}
                  {session.addons && session.addons.length > 0 && (
                    <div className="flex items-center gap-2 text-xs">
                      <span className="bg-orange-500/20 text-orange-400 px-2 py-0.5 rounded-full">
                        Add-ons: {session.addons.map(a => a.name).join(', ')} (+${session.addons.reduce((sum, a) => sum + (a.price || 0), 0)})
                      </span>
                    </div>
                  )}

                  {/* Notes */}
                  {session.notes && (
                    <div className="p-2 sm:p-3 bg-black/30 rounded-lg">
                      <p className="text-xs sm:text-sm text-gray-300"><span className="font-semibold">Notes:</span> {session.notes}</p>
                    </div>
                  )}
                  
                  {/* Reschedule Info */}
                  {session.reschedule_count > 0 && (
                    <div className="p-2 sm:p-3 bg-yellow-900/20 border border-yellow-500/30 rounded-lg">
                      <p className="text-xs text-yellow-400">
                        🔄 Rescheduled {session.reschedule_count} time(s). {2 - session.reschedule_count} reschedule(s) remaining.
                      </p>
                    </div>
                  )}
                </div>

                {/* Right Side - Price & Status */}
                <div className="flex sm:flex-col justify-between sm:justify-start w-full sm:w-auto sm:text-right space-y-0 sm:space-y-2 flex-shrink-0">
                  <div className="text-2xl sm:text-3xl font-bold text-green-400">
                    ${session.price}
                  </div>
                  {session.is_member && (
                    <div data-testid={`session-member-badge-${session.id}`} className="inline-block text-[10px] font-black px-2 py-0.5 rounded-full bg-yellow-400 text-black">
                      MEMBER
                    </div>
                  )}
                  {session.bonus_hours > 0 && (
                    <div data-testid={`session-bonus-hours-${session.id}`} className="text-xs text-fuchsia-300">
                      +{session.bonus_hours} FREE hr (deal)
                    </div>
                  )}
                  {session.credit_discount > 0 && (
                    <div data-testid={`session-credits-${session.id}`} className="text-xs text-yellow-300 space-y-0.5">
                      {session.original_price > 0 && (
                        <div className="line-through text-gray-300">${Number(session.original_price).toFixed(2)}</div>
                      )}
                      <div>
                        {Number(session.hours_covered_by_credits || 0)} free hr{Number(session.hours_covered_by_credits) === 1 ? '' : 's'} used (-${Number(session.credit_discount).toFixed(2)})
                      </div>
                    </div>
                  )}
                  
                  {/* Amount Received Display */}
                  {session.status === 'confirmed' && (
                    <div className="text-xs text-gray-400 space-y-1">
                      {session.deposit > 0 && (
                        <div className="flex items-center gap-1 justify-end">
                          <span className="text-green-400">✓</span>
                          <span>Deposit: ${session.deposit}</span>
                        </div>
                      )}
                      {session.amount_received && session.amount_received > (session.deposit || 0) && (
                        <div className="flex items-center gap-1 justify-end">
                          <span className="text-green-400">✓</span>
                          <span>Received: ${session.amount_received}</span>
                        </div>
                      )}
                      {session.payment_status === 'deposit_paid' && !session.amount_received && session.deposit > 0 && (
                        <div className="text-orange-400 text-xs">
                          Balance due: ${(session.price - (session.deposit || 0)).toFixed(2)}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Status Badges */}
                  <div className="flex sm:flex-col gap-1 sm:space-y-1">
                    {session.employee_confirmed && (
                      <div className="text-xs px-2 py-1 bg-green-500/20 text-green-400 border border-green-500/30 rounded whitespace-nowrap">
                        ✓ Employee
                      </div>
                    )}
                    {session.admin_confirmed && (
                      <div className="text-xs px-2 py-1 bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded whitespace-nowrap">
                        ✓ Admin
                      </div>
                    )}
                    {!session.employee_confirmed && statusFilter === 'upcoming' && (
                      <div className="text-xs px-2 py-1 bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 rounded whitespace-nowrap">
                        ⏳ Pending
                      </div>
                    )}
                    {/* Payment Status Badge */}
                    {session.payment_status === 'paid' ? (
                      <div className="text-xs px-2 py-1 bg-green-500/20 text-green-400 border border-green-500/30 rounded whitespace-nowrap">
                        💰 Paid Full
                      </div>
                    ) : session.payment_status === 'deposit_paid' ? (
                      <div className="text-xs px-2 py-1 bg-orange-500/20 text-orange-400 border border-orange-500/30 rounded whitespace-nowrap">
                        💵 Deposit: ${session.deposit || 50}
                      </div>
                    ) : session.deposit > 0 ? (
                      <div className="text-xs px-2 py-1 bg-orange-500/20 text-orange-400 border border-orange-500/30 rounded whitespace-nowrap">
                        💵 Deposit: ${session.deposit}
                      </div>
                    ) : session.stripe_payment_intent_id ? (
                      <div className="text-xs px-2 py-1 bg-green-500/20 text-green-400 border border-green-500/30 rounded whitespace-nowrap">
                        💳 Paid Online
                      </div>
                    ) : session.amount_paid > 0 ? (
                      <div className="text-xs px-2 py-1 bg-green-500/20 text-green-400 border border-green-500/30 rounded whitespace-nowrap">
                        💵 Paid: ${session.amount_paid}
                      </div>
                    ) : (
                      <div className="text-xs px-2 py-1 bg-red-500/20 text-red-400 border border-red-500/30 rounded whitespace-nowrap">
                        ⚠️ Unpaid
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Action Buttons (Full Width at Bottom) - Only for Upcoming Sessions */}
              {statusFilter === 'upcoming' && (
                <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-purple-500/20 flex flex-wrap gap-2">
                  <button
                    onClick={() => handleResendEmail(session)}
                    disabled={resendingEmail === session.id || processing}
                    className="flex-1 min-w-[100px] sm:min-w-[140px] px-3 sm:px-4 py-1.5 sm:py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm font-semibold rounded-lg transition-colors flex items-center justify-center gap-1 sm:gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span>📧</span>
                    <span className="hidden sm:inline">{resendingEmail === session.id ? 'Sending...' : 'Resend Email'}</span>
                    <span className="sm:hidden">{resendingEmail === session.id ? 'Sending...' : 'Resend'}</span>
                  </button>
                  
                  <button
                    onClick={() => setMoveSession(session)}
                    disabled={processing}
                    data-testid={`move-session-btn-${session.booking_number}`}
                    className="flex-1 min-w-[80px] px-3 sm:px-4 py-1.5 sm:py-2 bg-orange-500 hover:bg-orange-400 text-black text-xs sm:text-sm font-semibold rounded-lg transition-colors flex items-center justify-center gap-1 disabled:opacity-50"
                  >
                    <Clock size={14} />
                    <span>Move</span>
                  </button>

                  {session.reschedule_count < 2 && (
                    <button
                      onClick={() => openRescheduleModal(session)}
                      disabled={processing}
                      className="flex-1 min-w-[100px] sm:min-w-[140px] px-3 sm:px-4 py-1.5 sm:py-2 bg-purple-600 hover:bg-purple-700 text-white text-xs sm:text-sm font-semibold rounded-lg transition-colors flex items-center justify-center gap-1 sm:gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <RefreshCw size={14} className="sm:w-4 sm:h-4" />
                      <span>Reschedule</span>
                    </button>
                  )}
                  
                  <button
                    onClick={() => openChangeEngineerModal(session)}
                    disabled={processing}
                    className="flex-1 min-w-[100px] sm:min-w-[140px] px-3 sm:px-4 py-1.5 sm:py-2 bg-orange-600 hover:bg-orange-700 text-white text-xs sm:text-sm font-semibold rounded-lg transition-colors flex items-center justify-center gap-1 sm:gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <UserCog size={14} className="sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">Change Engineer</span>
                    <span className="sm:hidden">Engineer</span>
                  </button>
                  
                  {session.status === 'pending_approval' && (
                    <button
                      onClick={() => handleAdminConfirm(session.id)}
                      disabled={processing}
                      className="w-full px-4 py-3 bg-green-600 hover:bg-green-700 text-white text-sm sm:text-base font-bold rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed animate-pulse shadow-lg"
                    >
                      <Check size={18} className="sm:w-5 sm:h-5" />
                      <span>Approve Booking</span>
                    </button>
                  )}
                  
                  {session.status === 'confirmed' && !session.admin_confirmed && (
                    <button
                      onClick={() => handleAdminConfirm(session.id)}
                      disabled={processing}
                      className="w-full px-4 py-3 bg-green-600 hover:bg-green-700 text-white text-sm sm:text-base font-bold rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                    >
                      <Check size={18} className="sm:w-5 sm:h-5" />
                      <span>Confirm Booking</span>
                    </button>
                  )}
                  
                  {/* Decline Button - Only available for pending_approval bookings, NOT confirmed */}
                  {session.status === 'pending_approval' && (
                    <button
                      onClick={() => handleDeclineBooking(session)}
                      disabled={processing}
                      className="w-full px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <XCircle size={16} />
                      <span>Decline Booking</span>
                    </button>
                  )}

                  
                  {/* Mark Deposit Paid Button - For custom bookings pending payment */}
                  {session.payment_status === 'pending' && session.created_by === 'admin' && (
                    <button
                      onClick={() => handleMarkDepositPaid(session)}
                      disabled={processing}
                      className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <DollarSign size={16} />
                      <span>Mark Deposit Paid</span>
                    </button>
                  )}
                  
                  {/* Mark as Paid Button - Only show if not fully paid */}
                  {session.payment_status !== 'paid' && session.status === 'confirmed' && session.payment_status !== 'pending' && (
                    <button
                      onClick={() => handleMarkPaid(session)}
                      disabled={processing}
                      className="w-full px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <DollarSign size={16} />
                      <span>Mark Balance Paid</span>
                    </button>
                  )}

                </div>
              )}
              
              {/* Action Buttons for Completed Sessions */}
              {statusFilter === 'completed' && (
                <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-purple-500/20 flex flex-wrap gap-2">
                  {/* Record Final Payment Button - only if not fully paid */}
                  {session.payment_status !== 'paid' && (
                    <button
                      onClick={() => openPaymentModal(session)}
                      disabled={processing}
                      className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <DollarSign size={16} />
                      <span>Record Final Payment</span>
                    </button>
                  )}
                  
                  {/* View Details / Already Paid indicator */}
                  {session.payment_status === 'paid' && (
                    <div className="flex-1 px-4 py-2 bg-green-800/30 border border-green-500/30 text-green-400 text-sm font-semibold rounded-lg flex items-center justify-center gap-2">
                      <Check size={16} />
                      <span>Fully Paid - ${session.amount_received || session.price}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      
      {/* Reschedule Modal - Multi-Slot or Manual */}
      {showRescheduleModal && selectedSession && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-3 sm:p-4">
          <div className="bg-gradient-to-br from-gray-900 to-black border border-purple-500/30 rounded-xl p-4 sm:p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <h3 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
                <RefreshCw size={20} className="sm:w-6 sm:h-6 text-purple-400" />
                Reschedule Session
              </h3>
              <button onClick={() => setShowRescheduleModal(false)} className="text-gray-400 hover:text-white">
                <X size={20} className="sm:w-6 sm:h-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Client Info */}
              <div className="bg-black/40 rounded-lg p-3">
                <p className="text-gray-400 text-sm">
                  Client: <span className="text-white font-semibold">{selectedSession.name}</span>
                </p>
                <p className="text-gray-400 text-sm mt-1">
                  Current: <span className="text-red-400">{(() => {
                    const [year, month, day] = selectedSession.date.split('-').map(Number);
                    return new Date(year, month - 1, day).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                  })()} at {selectedSession.time}</span>
                  <span className="text-green-400 text-xs ml-2">(will be reopened)</span>
                </p>
              </div>
              
              {/* Mode Toggle */}
              <div className="flex gap-2">
                <button
                  onClick={() => setIsManualReschedule(false)}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-semibold transition-all ${
                    !isManualReschedule 
                      ? 'bg-purple-600 text-white' 
                      : 'bg-purple-900/30 text-gray-400 hover:text-white'
                  }`}
                >
                  📧 Send Options
                </button>
                <button
                  onClick={() => setIsManualReschedule(true)}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-semibold transition-all ${
                    isManualReschedule 
                      ? 'bg-green-600 text-white' 
                      : 'bg-green-900/30 text-gray-400 hover:text-white'
                  }`}
                >
                  ✏️ Manual Override
                </button>
              </div>
              
              {!isManualReschedule ? (
                <>
                  {/* Slot Options Mode */}
                  <div>
                    <label className="block text-gray-300 text-sm font-semibold mb-2">
                      Offer Time Slot Options <span className="text-gray-500">(client will choose one)</span>
                    </label>
                    
                    <div className="space-y-2">
                      {slotOptions.map((slot, index) => (
                        <div key={index} className="flex gap-2 items-center">
                          <span className="text-purple-400 font-bold w-6">{index + 1}.</span>
                          <input
                            type="date"
                            value={slot.date}
                            onChange={(e) => updateSlotOption(index, 'date', e.target.value)}
                            className="flex-1 px-3 py-2 text-sm bg-black/40 border border-purple-500/30 rounded-lg text-white focus:outline-none focus:border-purple-500"
                          />
                          <select
                            value={slot.time}
                            onChange={(e) => updateSlotOption(index, 'time', e.target.value)}
                            className="w-32 px-3 py-2 text-sm bg-black/40 border border-purple-500/30 rounded-lg text-white focus:outline-none focus:border-purple-500"
                          >
                            <option value="">Time</option>
                            {['9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM', '6:00 PM', '7:00 PM', '8:00 PM', '9:00 PM', '10:00 PM', '11:00 PM', '12:00 AM', '1:00 AM', '2:00 AM', '3:00 AM', '4:00 AM'].map(t => (
                              <option key={t} value={t}>{t}</option>
                            ))}
                          </select>
                          {slotOptions.length > 1 && (
                            <button
                              onClick={() => removeSlotOption(index)}
                              className="p-2 text-red-400 hover:text-red-300"
                            >
                              <X size={16} />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    {slotOptions.length < 5 && (
                      <button
                        onClick={addSlotOption}
                        className="mt-2 text-purple-400 hover:text-purple-300 text-sm flex items-center gap-1"
                      >
                        + Add another option
                      </button>
                    )}
                  </div>
                  
                  {/* Info Box */}
                  <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3">
                    <p className="text-blue-300 text-xs">
                      📧 Client will receive an email with these options to choose from.
                    </p>
                    <p className="text-blue-300 text-xs mt-1">
                      ⏰ Offered slots will be locked for 1 hour while client decides.
                    </p>
                    <p className="text-green-300 text-xs mt-1">
                      ✅ Current slot ({selectedSession.time}) will be reopened immediately.
                    </p>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-4">
                    <button
                      onClick={handleReschedule}
                      disabled={processing || !slotOptions.some(s => s.date && s.time)}
                      className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg text-sm font-semibold hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50"
                    >
                      {processing ? 'Sending...' : `Send ${slotOptions.filter(s => s.date && s.time).length} Options to Client`}
                    </button>
                    <button
                      onClick={() => setShowRescheduleModal(false)}
                      disabled={processing}
                      className="px-4 py-3 bg-gray-600 text-white rounded-lg text-sm font-semibold hover:bg-gray-700 transition-all disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {/* Manual Override Mode */}
                  <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-3 mb-3">
                    <p className="text-green-300 text-xs">
                      ✏️ <strong>Manual Override:</strong> Directly reschedule this booking (e.g., client confirmed via text/phone).
                    </p>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="block text-gray-300 text-sm font-semibold mb-1">New Date</label>
                      <input
                        type="date"
                        value={manualDate}
                        onChange={(e) => handleManualDateChange(e.target.value)}
                        className="w-full px-3 py-2 text-sm bg-black/40 border border-green-500/30 rounded-lg text-white focus:outline-none focus:border-green-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-gray-300 text-sm font-semibold mb-1">
                        New Time
                        {loadingAvailability && <span className="text-gray-500 ml-2 text-xs">(checking availability...)</span>}
                      </label>
                      <select
                        value={manualTime}
                        onChange={(e) => setManualTime(e.target.value)}
                        className="w-full px-3 py-2 text-sm bg-black/40 border border-green-500/30 rounded-lg text-white focus:outline-none focus:border-green-500"
                        disabled={!manualDate}
                      >
                        <option value="">Select time</option>
                        {['9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM', '6:00 PM', '7:00 PM', '8:00 PM', '9:00 PM', '10:00 PM', '11:00 PM', '12:00 AM', '1:00 AM', '2:00 AM', '3:00 AM', '4:00 AM'].map(t => {
                          const isBooked = bookedSlots.includes(t);
                          return (
                            <option 
                              key={t} 
                              value={t} 
                              disabled={isBooked}
                              className={isBooked ? 'text-red-400' : ''}
                            >
                              {t}{isBooked ? ' (BOOKED)' : ''}
                            </option>
                          );
                        })}
                      </select>
                      {bookedSlots.length > 0 && (
                        <p className="text-xs text-orange-400 mt-1">
                          ⚠️ {bookedSlots.length} time slot(s) already booked on this date
                        </p>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2 bg-black/30 p-2 rounded-lg">
                      <input
                        type="checkbox"
                        id="sendConfirmation"
                        checked={sendConfirmation}
                        onChange={(e) => setSendConfirmation(e.target.checked)}
                        className="w-4 h-4"
                        style={{ accentColor: '#10b981' }}
                      />
                      <label htmlFor="sendConfirmation" className="text-sm text-gray-300 cursor-pointer">
                        Send confirmation email to client
                      </label>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-4">
                    <button
                      onClick={handleManualReschedule}
                      disabled={processing || !manualDate || !manualTime}
                      className="flex-1 px-4 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg text-sm font-semibold hover:from-green-700 hover:to-emerald-700 transition-all disabled:opacity-50"
                    >
                      {processing ? 'Rescheduling...' : '✅ Confirm Reschedule'}
                    </button>
                    <button
                      onClick={() => setShowRescheduleModal(false)}
                      disabled={processing}
                      className="px-4 py-3 bg-gray-600 text-white rounded-lg text-sm font-semibold hover:bg-gray-700 transition-all disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Change Engineer Modal */}
      {showChangeEngineerModal && selectedSession && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-3 sm:p-4">
          <div className="bg-gradient-to-br from-gray-900 to-black border border-purple-500/30 rounded-xl p-4 sm:p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <h3 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
                <UserCog size={20} className="sm:w-6 sm:h-6 text-orange-400" />
                Change Engineer
              </h3>
              <button onClick={() => setShowChangeEngineerModal(false)} className="text-gray-400 hover:text-white">
                <X size={20} className="sm:w-6 sm:h-6" />
              </button>
            </div>
            
            <div className="space-y-3 sm:space-y-4">
              <div>
                <p className="text-gray-400 text-xs sm:text-sm mb-2">
                  Client: <span className="text-white font-semibold">{selectedSession.name}</span>
                </p>
                <p className="text-gray-400 text-xs sm:text-sm mb-2">
                  Current Engineer: <span className="text-white font-semibold">{selectedSession.engineer_name}</span>
                </p>
              </div>
              
              <div>
                <label className="block text-gray-300 text-xs sm:text-sm font-semibold mb-1.5">Select New Engineer</label>
                <select
                  value={newEngineerId}
                  onChange={(e) => setNewEngineerId(parseInt(e.target.value))}
                  className="w-full px-3 py-2 text-sm bg-black/40 border border-purple-500/30 rounded-lg text-white focus:outline-none focus:border-purple-500"
                >
                  {engineers.map((eng) => (
                    <option key={eng.id} value={eng.id}>
                      {eng.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-4 sm:mt-6">
                <button
                  onClick={handleChangeEngineer}
                  disabled={processing}
                  className="flex-1 px-4 py-2.5 sm:py-3 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-lg text-sm sm:text-base font-semibold hover:from-orange-700 hover:to-red-700 transition-all disabled:opacity-50"
                >
                  {processing ? 'Changing...' : 'Confirm Change'}
                </button>
                <button
                  onClick={() => setShowChangeEngineerModal(false)}
                  disabled={processing}
                  className="px-4 py-2.5 sm:py-3 bg-gray-600 text-white rounded-lg text-sm sm:text-base font-semibold hover:bg-gray-700 transition-all disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Recording Modal */}
      {showPaymentModal && selectedSession && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-3 sm:p-4">
          <div className="bg-gradient-to-br from-gray-900 to-black border border-green-500/30 rounded-xl p-4 sm:p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
                <DollarSign size={20} className="text-green-400" />
                Record Final Payment
              </h3>
              <button onClick={() => setShowPaymentModal(false)} className="text-gray-400 hover:text-white">
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Session Info */}
              <div className="bg-gray-800/50 rounded-lg p-3">
                <p className="text-gray-400 text-sm">Client: <span className="text-white font-semibold">{selectedSession.name}</span></p>
                <p className="text-gray-400 text-sm">Session: <span className="text-white">{selectedSession.service_name}</span></p>
                <p className="text-gray-400 text-sm">Total Price: <span className="text-green-400 font-bold">${selectedSession.price}</span></p>
                {selectedSession.addons && selectedSession.addons.length > 0 && (
                  <p className="text-gray-400 text-sm">Add-ons: <span className="text-orange-400">{selectedSession.addons.map(a => a.name).join(', ')} (+${selectedSession.addons.reduce((s, a) => s + (a.price || 0), 0)})</span></p>
                )}
                {selectedSession.deposit && (
                  <p className="text-gray-400 text-sm">Deposit Paid: <span className="text-green-400">${selectedSession.deposit}</span></p>
                )}
              </div>
              
              {/* Payment Amount */}
              <div>
                <label className="block text-gray-300 text-sm font-semibold mb-1.5">Amount Received ($)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-green-500 focus:outline-none"
                  placeholder="Enter amount received"
                />
              </div>
              
              {/* Payment Method */}
              <div>
                <label className="block text-gray-300 text-sm font-semibold mb-1.5">Payment Method</label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-green-500 focus:outline-none"
                >
                  <option value="cash">Cash</option>
                  <option value="zelle">Zelle</option>
                  <option value="venmo">Venmo</option>
                  <option value="cashapp">Cash App</option>
                  <option value="stripe">Stripe</option>
                  <option value="check">Check</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              {/* Submit Button */}
              <button
                onClick={handleRecordPayment}
                disabled={processing || !paymentAmount}
                className="w-full px-4 py-3 bg-green-600 hover:bg-green-700 text-white font-bold rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {processing ? (
                  <>Processing...</>
                ) : (
                  <>
                    <Check size={18} />
                    Record Payment
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UpcomingSessionsCalendar;
